import {
  MonthLock,
  MonthLockScope,
  MonthUnlockDto,
  RED_RULES,
  addDaysToDay,
  monthEndDay,
  plantDay,
  redEditWindow,
  redGraceDays,
  refusesEdits,
  shiftMonth,
} from '@anessa/shared-models';

/**
 * Where one month stands for the `~lockstatus` dev dialog:
 * - `open`: hasn't ended yet;
 * - `red_editable`: closed, still inside its RED edit window;
 * - `closed_unlocked`: its RED window has passed but it isn't locked yet
 *   (a month of a plant whose first lock hasn't happened);
 * - `locked_reviewed`: locked and RED-reviewed (shown from its snapshot).
 * Months before the RED start month are summed up in `olderNote`, not listed.
 */
export type MonthLockRowStatus = 'open' | 'red_editable' | 'closed_unlocked' | 'locked_reviewed';

export interface MonthLockStatusRow {
  month: string;
  status: MonthLockRowStatus;
  /** Last RED-editable day (`YYYY-MM-DD`) for a `red_editable` month. */
  editableUntil: string | null;
  /** The viewer is on an escape-hatch unlock of this month. */
  unlockedForYou: boolean;
}

export interface MonthLockStatus {
  startMonth: string | null;
  lockedMonth: string | null;
  lockedThrough: string | null;
  /** The next month to lock and the day it does (the day after its RED window closes); null when nothing will. */
  nextLock: { month: string; on: string } | null;
  rows: MonthLockStatusRow[];
  /** What's true of every month older than the last row, or null when nothing older matters. */
  olderNote: string | null;
  /** Which kinds of edit the lock refuses today (`RED_RULES.editLockFrom`, ADEV-1251). */
  editNote: string;
}

/** At most this many unlocked months: a plant whose first lock is still ahead has all of them unlocked. */
const MAX_UNLOCKED_ROWS = 3;
/** At most this many rows in all. */
const MAX_ROWS = 12;

/**
 * The plant's month-lock picture at `now`, newest month first: the latest
 * unlocked months, then the locked ones down to the RED start month, capped,
 * with a note for everything older. Mirrors `monthLock` and `redEditWindow`
 * (shared with the server), so it shows exactly what is enforced.
 */
export function buildMonthLockStatus(
  lock: MonthLock,
  plantId: string,
  unlock: MonthUnlockDto | null,
  now: Date = new Date(),
): MonthLockStatus {
  const { startMonth, lockedMonth, lockedThrough } = lock;
  const unlockedMonth = unlock?.plantId === plantId ? unlock.month : null;
  const row = (month: string, status: MonthLockRowStatus): MonthLockStatusRow => ({
    month,
    status,
    editableUntil: status === 'red_editable' ? redEditWindow(month, now, startMonth).editableUntil : null,
    unlockedForYou: month === unlockedMonth,
  });

  const rows: MonthLockStatusRow[] = [];
  let month = plantDay(now).slice(0, 7);
  // Unlocked months: newer than the locked month (or all of them, before any lock).
  while (rows.length < MAX_UNLOCKED_ROWS && (!lockedMonth || month > lockedMonth)) {
    rows.push(row(month, unlockedStatus(month, now, startMonth)));
    month = shiftMonth(month, -1);
  }
  // Skip any further unlocked months the cap left out, down to the lock.
  if (lockedMonth) {
    while (month > lockedMonth) month = shiftMonth(month, -1);
  }
  // Locked, RED-reviewed months: the locked month down to the start month.
  while (lockedMonth && startMonth && rows.length < MAX_ROWS && month >= startMonth) {
    rows.push(row(month, 'locked_reviewed'));
    month = shiftMonth(month, -1);
  }

  return {
    startMonth,
    lockedMonth,
    lockedThrough,
    nextLock: nextLock(lock),
    rows,
    olderNote: olderNote(lock, month),
    editNote: editNote(now),
  };
}

/** A locked month refuses only the kinds of edit `RED_RULES.editLockFrom` names, from its day on. */
function editNote(now: Date): string {
  const kinds = (Object.keys(RED_RULES.editLockFrom) as MonthLockScope[]).map((scope) => {
    const from = RED_RULES.editLockFrom[scope];
    if (from === null) return `${scope} never`;
    return refusesEdits(scope, now) ? `${scope} refused` : `${scope} from ${from}`;
  });
  return `Edits into locked months: ${kinds.join(', ')}. One let through into a RED-reviewed month queues its RED re-check.`;
}

function unlockedStatus(month: string, now: Date, startMonth: string | null): MonthLockRowStatus {
  const status = redEditWindow(month, now, startMonth).status;
  if (status === 'open') return 'open';
  return status === 'editable' ? 'red_editable' : 'closed_unlocked';
}

/** The next month to lock and its lock day: the day after that month's RED edit window closes. */
function nextLock(lock: MonthLock): { month: string; on: string } | null {
  if (!lock.startMonth || !RED_RULES.monthLockEnabled) return null;
  const month = lock.lockedMonth ? shiftMonth(lock.lockedMonth, 1) : lock.startMonth;
  return { month, on: addDaysToDay(monthEndDay(month), redGraceDays(month, lock.startMonth) + 1) };
}

/** What holds for every month older than `oldestNotShown` (the month after the last row). */
function olderNote(lock: MonthLock, oldestNotShown: string): string | null {
  if (!lock.startMonth) return 'This plant has no RED start month: nothing locks.';
  if (!RED_RULES.monthLockEnabled) return 'The month lock is switched off (RED_RULES.monthLockEnabled).';
  if (!lock.lockedMonth) return null;
  if (oldestNotShown >= lock.startMonth) {
    return `Locked and RED-reviewed from ${lock.startMonth} through ${oldestNotShown}; earlier months are locked before RED review.`;
  }
  return `Everything up to ${oldestNotShown} is locked, before RED review started (${lock.startMonth}).`;
}
